import { motion } from "framer-motion";
import { Flame, Wheat, Martini } from "lucide-react";
import { IMAGES } from "@/lib/site";

const CHAPTERS = [
  {
    num: "01",
    title: "The Heritage",
    icon: Flame,
    text: "Recipes carried across the border in memory, not on paper. Every salsa, every mole, every marinade begins the way Alejandra's grandmother made it — by hand, by heart, by taste.",
    image: IMAGES.interiorDining,
    caption: "The dining room, Aurora",
  },
  {
    num: "02",
    title: "Masa & Fire",
    icon: Wheat,
    text: "Corn pressed into tortillas every morning. Fajitas that announce themselves before they reach the table. Meats marinated overnight and kissed by open flame until the edges char just so.",
    image: IMAGES.fajitas,
    caption: "Texas fajitas, still singing",
  },
  {
    num: "03",
    title: "The Agave Spirit",
    icon: Martini,
    text: "Hand-shaken margaritas built on fresh lime and real agave — never a mix. Pull up a stool, order the top shelf, and stay a while. This is a family place, and now you're family.",
    image: IMAGES.margarita,
    caption: "House margarita, hand-shaken",
  },
];

const reveal = {
  hidden: { opacity: 0, y: 48 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export default function Manifesto() {
  return (
    <section id="story" className="relative py-28 sm:py-40" data-testid="manifesto-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="mb-24 max-w-2xl"
        >
          <p className="font-mono-d text-[11px] uppercase tracking-[0.3em] text-terracotta mb-6" data-testid="manifesto-overline">
            Our Story — In Three Chapters
          </p>
          <h2 className="font-serif-d text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-cream leading-[1.05]" data-testid="manifesto-title">
            Family owned. Family run. <span className="italic text-terracotta">For families.</span>
          </h2>
        </motion.div>

        <div className="space-y-28 sm:space-y-40">
          {CHAPTERS.map((c, i) => (
            <motion.article
              key={c.num}
              variants={reveal}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-100px" }}
              className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-center ${i % 2 === 1 ? "" : ""}`}
              data-testid={`manifesto-chapter-${c.num}`}
            >
              <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="flex items-baseline gap-5 mb-6">
                  <span className="font-mono-d text-sm text-terracotta tracking-[0.2em]">{c.num}</span>
                  <span className="h-px flex-1 bg-terracotta/25" />
                  <c.icon className="w-5 h-5 text-terracotta/70" />
                </div>
                <h3 className="font-serif-d text-3xl sm:text-4xl lg:text-5xl font-semibold text-cream tracking-tight mb-6">
                  {c.title}
                </h3>
                <p className="text-base leading-relaxed text-cream/65 font-light max-w-md">{c.text}</p>
              </div>
              <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <div className="relative group">
                  <div className="absolute -inset-3 border border-terracotta/20 rounded-2xl translate-x-3 translate-y-3 transition-transform duration-700 group-hover:translate-x-1.5 group-hover:translate-y-1.5" />
                  <div className="relative overflow-hidden rounded-2xl clipped-frame">
                    <img
                      src={c.image}
                      alt={c.caption}
                      loading="lazy"
                      className="w-full h-[320px] sm:h-[440px] object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-obsidian/15 group-hover:bg-obsidian/0 transition-colors duration-700" />
                  </div>
                  <p className="mt-4 font-mono-d text-[10px] uppercase tracking-[0.25em] text-cream/40">
                    {c.caption}
                  </p>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
